'use client'

// The design system's status pill (.status) and plain tag: the one way a
// state or a label is shown anywhere in the backoffice. A pill is 9 px
// mono, a 4 px dot in its own colour, on its surface; a tag is the same
// shape without a dot, on the secondary surface.

export type PillTone = 'success' | 'warning' | 'danger' | 'signal' | 'neutral'

const PILL: Record<PillTone, string> = {
  success: 'bg-success-surface text-success',
  warning: 'bg-warning-surface text-warning',
  danger: 'bg-danger-surface text-destructive',
  signal: 'bg-signal-surface text-signal',
  neutral: 'bg-secondary text-muted-foreground',
}

export function StatusPill({ tone, children, dot = true, title, className = '' }: {
  tone: PillTone
  children: React.ReactNode
  dot?: boolean
  title?: string
  className?: string
}) {
  return (
    <span title={title} className={`inline-flex items-center gap-[5px] whitespace-nowrap rounded-[4px] px-[7px] py-[3px] font-mono text-micro font-medium leading-none ${PILL[tone]} ${className}`}>
      {dot && <span className="h-1 w-1 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  )
}

export function Tag({ children, title, className = '', mono = true }: { children: React.ReactNode; title?: string; className?: string; mono?: boolean }) {
  return (
    <span title={title} className={`inline-flex items-center whitespace-nowrap rounded-[4px] border border-border bg-secondary px-[6px] py-[2px] text-micro font-medium leading-none text-muted-foreground ${mono ? 'font-mono' : ''} ${className}`}>
      {children}
    </span>
  )
}

/** Maps the backoffice's older tone words onto the pill tones. */
export function pillTone(t: string | null | undefined): PillTone {
  switch ((t ?? '').toLowerCase()) {
    case 'green': case 'emerald': case 'success': case 'ok': case 'online': case 'succeeded': case 'fulfilled': case 'resolved': case 'enabled': return 'success'
    case 'amber': case 'warning': case 'queued': case 'pending': case 'paused': case 'stale': return 'warning'
    case 'red': case 'danger': case 'error': case 'failed': case 'offline': case 'stalled': case 'expired': return 'danger'
    case 'sky': case 'blue': case 'signal': case 'running': case 'info': case 'live': return 'signal'
    default: return 'neutral'
  }
}
